import type {
  DashboardToday,
  TodayCounts,
  TodaySubscription,
  TodaySmsAlert,
  TodayChannelAlert,
} from "../../api/dashboardService";

/** Role route-permission key for the Dashboard's account alerts (backend DASHBOARD_KEYS.OWNER). */
export const DASHBOARD_OWNER_KEY = 101;

/** Refetch cadence while the page is open. */
export const REFRESH_MS = 60_000;

/** "14:05" / "14:05:00" → "2:05 PM"; anything else is shown as stored. */
export function formatClock(time: string | null | undefined): string {
  const m = String(time ?? "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return String(time ?? "");
  const h = Number(m[1]);
  if (h > 23) return String(time);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

export const SOURCE_LABEL: Record<string, string> = {
  appointment: "Booked",
  walk_in: "Walk-in",
  other: "Other",
  online: "Online",
};

export function sourceLabel(source: string | null | undefined): string {
  return SOURCE_LABEL[String(source ?? "")] || "Booked";
}

/** "Just arrived", "12 min", "1 h 05 min"; null when the arrival time is unknown. */
export function waitLabel(minutes: number | null | undefined): string {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) return "Time unknown";
  if (minutes < 1) return "Just arrived";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  return `${h} h ${String(minutes % 60).padStart(2, "0")} min`;
}

export interface StripTile {
  key: keyof TodayCounts;
  label: string;
  hint: string;
  tone: "neutral" | "info" | "warn" | "good" | "bad";
}

/** The Today strip, left to right. Same buckets as the Appointments page status cards. */
export const STRIP_TILES: StripTile[] = [
  { key: "booked", label: "Booked", hint: "All of today's appointments except cancelled", tone: "neutral" },
  { key: "toCome", label: "Still to come", hint: "Not arrived yet", tone: "info" },
  { key: "waiting", label: "Waiting", hint: "In the waiting room or in queue", tone: "warn" },
  { key: "inChair", label: "In chair", hint: "Treatment in progress", tone: "info" },
  { key: "done", label: "Done", hint: "Completed today", tone: "good" },
  { key: "noShow", label: "No-show", hint: "Marked as no-show today", tone: "bad" },
];

/** "3 cancelled · 2 walk-ins · 1 online", parts with zero left out. */
export function stripFootnote(counts: TodayCounts | null | undefined): string {
  if (!counts) return "";
  const parts: string[] = [];
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  if (counts.cancelled) parts.push(plural(counts.cancelled, "cancelled", "cancelled"));
  if (counts.walkIn) parts.push(plural(counts.walkIn, "walk-in", "walk-ins"));
  if (counts.online) parts.push(plural(counts.online, "online booking", "online bookings"));
  if (counts.other) parts.push(plural(counts.other, "other", "other"));
  return parts.join(" · ");
}

export interface AccountBanner {
  key: "sms" | "whatsapp" | "email" | "subscription";
  tone: "warn" | "bad";
  text: string;
  action: { label: string; to: string } | null;
}

/** Owner banners: SMS credits running out, subscription due or overdue. Nothing when all is fine. */
export function accountBanners(
  sms: TodaySmsAlert | null | undefined,
  subscription: TodaySubscription | null | undefined,
  channelAlerts: TodayChannelAlert[] | null | undefined = []
): AccountBanner[] {
  const out: AccountBanner[] = [];
  if (sms?.enabled && sms.showAlert && (sms.level === "depleted" || sms.level === "low")) {
    out.push({
      key: "sms",
      tone: sms.level === "depleted" ? "bad" : "warn",
      text:
        sms.level === "depleted"
          ? "SMS credits have run out — appointment and invoice SMS are not being sent."
          : `SMS credits are running low: ${sms.tokenCredits ?? 0} left.`,
      action: { label: "Buy SMS tokens", to: "/client/sms-topup" },
    });
  }
  (channelAlerts ?? []).forEach((alert) => {
    out.push({
      key: alert.channel,
      tone: alert.level === "depleted" ? "bad" : "warn",
      text:
        alert.level === "depleted"
          ? `${alert.label} tokens have run out — ${alert.label} messages are not being sent.`
          : `${alert.label} tokens are running low: ${alert.tokenCredits ?? 0} left.`,
      action: { label: `Buy ${alert.label} tokens`, to: alert.topUpPath },
    });
  });
  if (subscription?.status === "overdue") {
    out.push({ key: "subscription", tone: "bad", text: "The clinic subscription is overdue.", action: null });
  } else if (subscription?.status === "due_soon") {
    const d = subscription.daysToEnd;
    const when = typeof d === "number" ? (d === 0 ? "today" : `in ${d} day${d === 1 ? "" : "s"}`) : "soon";
    out.push({ key: "subscription", tone: "warn", text: `The clinic subscription is due ${when}.`, action: null });
  }
  return out;
}

/** "Due today" / "1 day overdue" / "4 days overdue". */
export function overdueLabel(days: number): string {
  if (days <= 0) return "Due today";
  return `${days} day${days === 1 ? "" : "s"} overdue`;
}

/**
 * Which "to do" blocks have something to show; empty ones are hidden so a quiet day reads short.
 * Without treatments (BookMyDentist only) the treatment blocks stay hidden even if old rows exist.
 */
export function visibleTodoBlocks(
  data: DashboardToday | null | undefined,
  { treatments = true }: { treatments?: boolean } = {}
) {
  if (!data) return { treatedNotBilled: false, onlineToResolve: false, unfinishedPlans: false, labDue: false, lowStock: false };
  return {
    treatedNotBilled: treatments && data.treatedNotBilled.length > 0,
    onlineToResolve: data.onlineToResolve.provisionalCount > 0 || data.onlineToResolve.unpaidOnlineToday.length > 0,
    unfinishedPlans: treatments && data.unfinishedPlans.length > 0,
    labDue: data.labDue.length > 0,
    lowStock: data.lowStock.enabled && data.lowStock.count > 0,
  };
}

/** "Mon 12 Oct" for the page header. */
export function headerDate(ymd: string): string {
  const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
  const mo = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getUTCMonth()];
  return `${wd} ${d.getUTCDate()} ${mo}`;
}
